import React from 'react'
import DOMPurify from 'isomorphic-dompurify'
import { cn } from '@/lib/utils'

interface SafeHtmlProps {
  content: string
  className?: string
  as?: React.ElementType
}

type DomPurifySanitizeConfig = NonNullable<Parameters<typeof DOMPurify.sanitize>[1]>

const SANITIZE_OPTIONS: DomPurifySanitizeConfig = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    'b',
    'i',
    'a',
    'ul',
    'ol',
    'li',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'code',
    'pre',
    'img',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'hr',
    'span',
    'div',
    'sub',
    'sup',
  ],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel', 'width', 'height'],
  ALLOW_DATA_ATTR: false,
}

export function SafeHtml({ content, className, as: Component = 'div' }: SafeHtmlProps) {
  if (!content) {
    return null
  }

  const sanitized = DOMPurify.sanitize(content, SANITIZE_OPTIONS)

  return (
    <Component
      className={cn(
        'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl max-w-none',
        'prose-headings:font-semibold prose-headings:text-foreground',
        'prose-p:text-muted-foreground prose-p:leading-relaxed',
        'prose-strong:text-foreground prose-strong:font-semibold',
        'prose-em:text-foreground prose-em:italic',
        'prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm',
        'prose-pre:bg-muted prose-pre:text-foreground prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto',
        'prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic',
        'prose-ul:list-disc prose-ul:pl-6',
        'prose-ol:list-decimal prose-ol:pl-6',
        'prose-li:text-muted-foreground',
        'prose-a:text-primary prose-a:underline prose-a:decoration-primary/50 hover:prose-a:decoration-primary',
        'prose-img:rounded-lg prose-img:shadow-md',
        'prose-table:border-collapse prose-table:w-full',
        'prose-th:border prose-th:border-border prose-th:p-2 prose-th:text-left prose-th:font-semibold',
        'prose-td:border prose-td:border-border prose-td:p-2',
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitized }}
      {...(Component === 'div' || Component === 'p' ? { style: { textWrap: 'pretty' } } : {})}
    />
  )
}
